import { createContext, useContext } from 'react';

// Provider the agent console authenticated through. Decides which backend JwtBearer scheme
// validates the token: 'vismaonline' (shared customer OIDC session, the pre-#1994 default) or
// 'entra' (the separate Microsoft Entra ID session, #1994).
export type AgentOidcProvider = 'vismaonline' | 'entra';

export interface AgentAuth {
    // Ref-stable getter: its identity is fixed for the provider's lifetime, so SignalR's
    // accessTokenFactory and the useEffect dependency arrays that hold it never churn on token
    // rotation (see useAccessToken's original contract).
    getAccessToken: () => string | null;
    // Ref-stable getter that renews when the cached token is missing or near expiry, else returns it
    // (falling back to the cached one if the renew fails). SignalR's accessTokenFactory awaits it on
    // every (re)connect so a socket the server aborts at token expiry reconnects with a fresh token,
    // not a stale ref the throttled background renew never refreshed.
    getFreshAccessToken: () => Promise<string | null>;
    oidcProvider: AgentOidcProvider;
    isLoading: boolean;
    isAuthenticated: boolean;
    // Entra mode only: the agent must trigger sign-in explicitly (there is no silent redirect like
    // the Visma Online flow's useAuthSetup). Undefined in OIDC mode, where AuthBootstrap drives it.
    signIn?: () => Promise<void>;
    // Entra mode only: drop the separate agent session and return to the sign-in screen. Powers both
    // the "log out" action in the agent console and the escape hatch on the not-authorized screen
    // (without it, signing in with a non-allow-listed account is a dead end — the cached session keeps
    // re-authenticating the same wrong account). Undefined in OIDC mode, which has no separate agent
    // session to drop (it rides the customer's shared Visma Online session).
    signOut?: () => Promise<void>;
    // Entra mode only: the username (email/UPN) of the signed-in account, surfaced on the
    // not-authorized screen so the agent can see which account lacks access. Undefined in OIDC mode.
    accountName?: string;
}

// Bridges the two auth modes so the rest of the console is provider-agnostic. Either
// OidcAgentAuthProvider (flag off) or EntraAgentAuthProvider (flag on) supplies the value.
export const AgentAuthContext = createContext<AgentAuth | undefined>(undefined);

export const useAgentAuth = (): AgentAuth => {
    const ctx = useContext(AgentAuthContext);
    if (ctx === undefined) {
        throw new Error('useAgentAuth must be used inside an agent auth provider');
    }
    return ctx;
};
